/**
 * Season Overview Algorithm
 *
 * Component-based Monte Carlo prediction using all current season races
 * with equal weighting. Conservative prediction for stable analysis.
 */

import { SEASON_OVERVIEW_CONFIG } from "../../prediction";
import {
  estimateConstructorParams,
  estimateDriverParams,
} from "../../prediction/component-model";
import { runDriverMonteCarlo } from "../../prediction/monte-carlo";
import { adjustParamsForStage } from "../../prediction/stage-adjustments";
import { getTrackClassification } from "../../statistics/track-classification";
import { calculateSimplePriceChange } from "../price-change";
import type {
  PredictionAlgorithm,
  PredictionResult,
  TeamCalculatorConstructor,
  TeamCalculatorDriver,
  TeamCalculatorRaceContext,
} from "../types";

function buildRaceContext(raceContext: TeamCalculatorRaceContext) {
  return {
    raceSlug: raceContext.raceName ?? "",
    trackType: getTrackClassification(raceContext.raceName ?? ""),
    isSprintWeekend: raceContext.isSprintWeekend,
    raceRound: raceContext.raceRound,
    season: 2025,
  };
}

export const seasonOverviewAlgorithm: PredictionAlgorithm = {
  metadata: {
    id: "season-overview",
    name: "Season Overview",
    description:
      "Conservative prediction using entire season performance with equal weighting across all races.",
    icon: "BarChart3",
  },

  predictDriver(
    driver: TeamCalculatorDriver,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const ctx = buildRaceContext(raceContext);
    const baseParams = estimateDriverParams(
      driver,
      ctx,
      SEASON_OVERVIEW_CONFIG,
    );
    const params = adjustParamsForStage(
      baseParams,
      driver,
      raceContext.currentStage,
    );
    const result = runDriverMonteCarlo(
      params,
      raceContext.isSprintWeekend,
      10_000,
      raceContext.season,
    );

    return {
      entityId: driver.driverSlug,
      predictedPoints: Math.round(result.expectedPoints * 10) / 10,
      confidence: result.confidence,
      p10: result.p10,
      p90: result.p90,
      stdDev: result.stdDev,
      dnfProb: result.dnfProb,
      components: result.components,
    };
  },

  predictConstructor(
    ctor: TeamCalculatorConstructor,
    raceContext: TeamCalculatorRaceContext,
  ): PredictionResult {
    const history = ctor.raceHistory;

    if (history.length === 0) {
      return {
        entityId: ctor.slug,
        predictedPoints: 20,
        confidence: 0.1,
      };
    }

    // For constructors: use weighted average from history + pit stop component
    const ctorParams = estimateConstructorParams(ctor, SEASON_OVERVIEW_CONFIG);
    const points = history.map((h) => h.totalPoints);
    let avgPoints = points.reduce((a, b) => a + b, 0) / points.length;

    // Sprint-aware: blend sprint-specific average when predicting for sprint weekend
    if (raceContext.isSprintWeekend) {
      const sprintRaces = history.filter((h) => h.isSprintWeekend);
      if (sprintRaces.length >= 2) {
        const sprintPts = sprintRaces.map((h) => h.totalPoints);
        const sprintAvg =
          sprintPts.reduce((a, b) => a + b, 0) / sprintPts.length;
        avgPoints = sprintAvg * 0.7 + avgPoints * 0.3;
      }
    }

    return {
      entityId: ctor.slug,
      predictedPoints: Math.round(avgPoints * 10) / 10,
      confidence: ctorParams.confidence,
    };
  },

  calculatePriceChange(
    predictedPoints: number,
    currentPrice: number,
    tier: "A" | "B",
    priceFloor: number,
  ): number {
    return calculateSimplePriceChange(
      predictedPoints,
      tier,
      currentPrice,
      priceFloor,
    );
  },
};
